import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Pagination } from '@mui/material'
import { FormattedMessage } from 'react-intl'

import EventCard from './components/EventCard/EventCard'
import { ReusableFilters } from '../ReusableFilters'
import { Preloader } from '../../../../components/UI'

import { isAuthenticated } from '../../../../helpers'
import useGetEvents from './hooks/useGetEvents'
import useGetAuthEvents from './hooks/useGetAuthEvents'

import styles from './styles.module.scss'
import { FILTERS_CONFIG } from './config'

export const EventsList = () => {
  const [pageEvents, setPageEvents] = useState(1)
  const [isAuthUser] = useState(isAuthenticated())
  const [defaultStatus, setDefaultStatus] = useState(null)

  const {
    eventsList: publicEventsList,
    isLoading: publicLoading,
    getEventsList,
  } = useGetEvents()
  const {
    eventsList: authEventsList,
    isLoading: authLoading,
    getAuthEventsList,
  } = useGetAuthEvents()

  const eventsList = useMemo(
    () => (isAuthUser ? authEventsList : publicEventsList),
    [isAuthUser, authEventsList, publicEventsList]
  )

  const isLoading = useMemo(
    () => (isAuthUser ? authLoading : publicLoading),
    [isAuthUser, authLoading, publicLoading]
  )

  const events = useMemo(() => eventsList?.events || [], [eventsList?.events])

  const defaultValues = useMemo(
    () => ({
      status: defaultStatus,
    }),
    [defaultStatus]
  )

  const paginationProps = useMemo(
    () => ({
      count: eventsList?.paginator?.lastPage || 0,
      page: eventsList?.paginator?.currentPage ?? 1,
    }),
    [eventsList?.paginator?.lastPage, eventsList?.paginator?.currentPage]
  )

  const showPagination = useMemo(
    () => eventsList?.paginator?.lastPage > 1 && events.length > 0,
    [eventsList?.paginator?.lastPage, events.length]
  )

  const showEventsGrid = useMemo(() => events.length > 0, [events.length])

  const handleGetEventsList = useCallback(() => {
    let urlParams = {}
    if (defaultStatus) {
      urlParams['eventStatusIds'] = defaultStatus.id
    }
    getEventsList(pageEvents, 10, urlParams)
  }, [getEventsList, defaultStatus, pageEvents])

  const handleGetAuthEventsList = useCallback(() => {
    let urlParams = {}
    if (defaultStatus) {
      urlParams['eventStatusIds'] = defaultStatus.id
    }
    getAuthEventsList(pageEvents, 10, urlParams)
  }, [getAuthEventsList, defaultStatus, pageEvents])

  const handleChangeEvents = useCallback((event, value) => {
    setPageEvents(value)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const handleFiltersChange = useCallback(({ status }) => {
    setDefaultStatus(status)
    setPageEvents(1) // Reset to first page when filters change
  }, [])

  useEffect(() => {
    if (isAuthUser) {
      return handleGetAuthEventsList()
    }

    handleGetEventsList()
  }, [isAuthUser, handleGetAuthEventsList, handleGetEventsList])

  if (isLoading && !eventsList) {
    return <Preloader />
  }

  return (
    <section className={styles.calendarList}>
      <div className={styles.layoutContainer}>
        <ReusableFilters
          filterConfigs={FILTERS_CONFIG}
          defaultValues={defaultValues}
          onFiltersChange={handleFiltersChange}
          isLoading={false}
          title='calendar.filters'
        />

        <div className={styles.content}>
          {showEventsGrid ? (
            <div className={styles.eventContainer}>
              {isLoading && (
                <div className={styles.filterLoaderOverlay}>
                  <Preloader />
                </div>
              )}

              <div className={styles.eventGrid}>
                {events.map(event => (
                  <EventCard {...event} key={event.id} />
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.emptyData}>
              <FormattedMessage id='calendar.noEvents' />
            </div>
          )}

          {showPagination && (
            <div className={styles.paginatorWrapper}>
              <Pagination
                classes={{ ul: styles.pagination }}
                count={paginationProps.count}
                page={paginationProps.page}
                onChange={handleChangeEvents}
                variant='outlined'
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default EventsList
